import { useRef } from "react";
import { api, commandId, reasonText, ApiError } from "../../lib/api";
export type ImportTarget = {
  fieldId: string;
  label: string;
  required: boolean;
  kind: string;
  dictCode: string | null;
};
export type Mapping = {
  id: string;
  name: string;
  columns: Record<string, string>;
  sourceRef: string;
  version: number;
};
export type Batch = {
  id: string;
  mappingName: string;
  mappingColumns?: Record<string, string>;
  status: "STAGED" | "COMMITTED" | "DISCARDED";
  rowCount: number;
  readyNewCount: number;
  readyUpdateCount: number;
  conflictCount: number;
  invalidCount: number;
  sourceRef: string;
  reason?: string;
  version: number;
  recordedAt: string;
  committedAt: string | null;
  discardedAt: string | null;
  discardedReason: string | null;
};
export type RowIssue = {
  column: string | null;
  fieldId: string | null;
  code: string;
  rawValue: string | null;
};
export type ReportRow = {
  lineNumber: number;
  outcome: string;
  rawValues: Record<string, string>;
  mappedValues: Record<string, string>;
  issues: RowIssue[];
  matchedPersonId: string | null;
  appliedAt: string | null;
};
export type Report = { batch: Batch; items: ReportRow[] };
export const BATCH_LABEL = { STAGED: "待确认", COMMITTED: "已入库", DISCARDED: "已作废" };
export const BATCH_TONE = {
  STAGED: "warning",
  COMMITTED: "success",
  DISCARDED: "neutral"
} as const;
export const OUTCOME_LABEL: Record<string, string> = {
  READY_NEW: "将新建",
  READY_UPDATE: "将补录会籍事实",
  CONFLICT: "需人工处理",
  INVALID: "需修改源文件",
  APPLIED: "已入库",
  SKIPPED: "未处理"
};
export const OUTCOME_TONE: Record<string, "brand" | "success" | "warning" | "danger" | "neutral"> =
  {
    READY_NEW: "brand",
    READY_UPDATE: "brand",
    CONFLICT: "warning",
    INVALID: "danger",
    APPLIED: "success",
    SKIPPED: "neutral"
  };
export const FIELD_LABEL: Record<string, string> = {
  "person.name": "姓名",
  "contact.mobile": "手机号",
  "membership.organization": "所属组织",
  "membership.approvedOn": "入会日期",
  "membership.sectorClassification": "界别分类",
  "membership.localNumber": "地方编号",
  "membership.centralNumber": "中央编号",
  "person.genderCode": "性别",
  "person.ethnicityCode": "民族",
  "person.politicalStatusCode": "政治面貌",
  // WO-113（2026-10-19）：会员基础信息表的其余十列。身份证号在报告与裁决页只以掩码出现。
  "person.birthDate": "出生日期",
  "person.nativePlace": "籍贯",
  "person.householdRegistration": "户籍所在地",
  "person.workStartOn": "参加工作时间",
  "person.idDocumentNumber": "身份证号码",
  "membership.representativeTier": "中上层",
  "membership.enterpriseScaleCode": "企业规模",
  "membership.isCentralEnterprise": "是否央企",
  "membership.postingDepartmentTypeCode": "实职安排部门类型",
  "membership.postingRankCode": "实职安排职级"
};
/** 按导入字段类型给填表人的一句提示；没有提示的类型就不加。 */
export const KIND_HINT: Record<string, string> = {
  boolean: "填 是/否",
  idDocument: "18 位，导入报告只显示掩码"
};
const ISSUE_LABEL: Record<string, string> = {
  REQUIRED_VALUE_MISSING: "必填项为空",
  INVALID_MOBILE: "手机号格式不正确",
  INVALID_DATE: "日期格式不能识别",
  UNRESOLVED_DICTIONARY_VALUE: "没有匹配的字典项",
  ORGANIZATION_NOT_FOUND_OR_DENIED: "组织不存在或不在管理范围内",
  VALUE_TOO_LONG: "内容超过长度限制",
  INVALID_BOOLEAN: "只认 是/否，请修改源文件",
  INVALID_ID_DOCUMENT_NUMBER: "身份证号格式或校验位不正确",
  MULTIPLE_EXISTING_MATCHES: "匹配到多份既有档案，请先核对并处理重复档案"
};
export function issueText(issue: RowIssue) {
  if (issue.code === "DUPLICATE_WITHIN_FILE")
    return `与源文件第 ${issue.rawValue ?? "另一"} 行重复，请核对两行后修改源文件。`;
  if (issue.code === 'APPLY_FAILED_ADJUDICATION_REQUIRED') return '匹配依据不足，请先人工裁决后再办理。';
  if (issue.code === 'APPLY_FAILED_DUPLICATE_NUMBER') return '编号与既有档案冲突，请核对来源后重新处理。';
  if (issue.code.startsWith("APPLY_FAILED_"))
    return `入库时未完整处理：${reasonText(new ApiError(409, issue.code.slice(13)))}`;
  return `${issue.column ? `「${issue.column}」` : ""}${ISSUE_LABEL[issue.code] ?? issue.code}${issue.rawValue ? `（原值：${issue.rawValue}）` : ""}`;
}
export function batchCounts(batch: Batch) {
  return batch.status === "COMMITTED"
    ? `已入库 ${batch.readyNewCount} · 需处理 ${batch.conflictCount + batch.invalidCount}`
    : batch.status === "DISCARDED"
      ? `预检通过 ${batch.readyNewCount + batch.readyUpdateCount} · 未入库 ${batch.rowCount}`
      : `将新建 ${batch.readyNewCount} · 将补录 ${batch.readyUpdateCount} · 需处理 ${batch.conflictCount + batch.invalidCount}`;
}
// Import commands record commandId, but stage/mapping do not deduplicate it. AccessAction provides uncertainty reconciliation.
export function useImportCommand() {
  const saved = useRef<{ key: string; id: string } | null>(null);
  return <T>(path: string, body: object) => {
    const key = JSON.stringify({ path, body });
    if (saved.current?.key !== key) saved.current = { key, id: commandId() };
    return api<T>(path, { method: "POST", body: { ...body, commandId: saved.current.id } });
  };
}
export function csvHeader(content: string): string[] {
  let cell = "",
    quoted = false;
  const cells: string[] = [];
  const text = content.replace(/^\uFEFF/, "");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      cells.push(cell.trim());
      cell = "";
    } else if (c === "\n") {
      cells.push(cell.trim());
      break;
    } else if (c !== "\r") cell += c;
    if (i === text.length - 1) cells.push(cell.trim());
  }
  if (quoted || !cells.length || cells.some((c) => !c) || new Set(cells).size !== cells.length)
    throw Error("请核对 CSV 第一行：表头不能为空或重名，引号须成对。");
  return cells;
}
